import { actions, containers, inputs, overlays } from '@ecosystem/newron-design-system';
import React, { useEffect, useState } from 'react';
import { FilterCriteria, FilterCriteriaGroup } from '../types';

const { Button, Chips } = actions;
const { Modal } = overlays;
const { Form } = containers;
const { FormControl, TextInput } = inputs;

const MAX_NAME_LENGTH = 64;
const ERROR_LONG_MESSAGE = `Please keep your name under ${MAX_NAME_LENGTH} characters`;
const ERROR_NO_FILTERS = 'At least one filter must be applied';

function isFiltersGrouped(
  filterCriteria: FilterCriteria[] | FilterCriteriaGroup[]
): filterCriteria is FilterCriteriaGroup[] {
  return filterCriteria?.length > 0 && filterCriteria[0].hasOwnProperty('filters');
}

interface SaveFilterModalProps {
  isOpen: boolean;
  filterCriteria: FilterCriteria[] | FilterCriteriaGroup[];
  onFilterNameChange?: (value: string) => void;
  onSave: (filterName: string) => void;
  onClose: () => void;
  filterNameErrorMessage?: string;
  useNewFilterLayout?: boolean;
}

export const SaveFilterModal = ({
  isOpen,
  filterCriteria,
  onFilterNameChange,
  onSave,
  onClose,
  filterNameErrorMessage,
  useNewFilterLayout,
}: SaveFilterModalProps) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [filterName, setFilterName] = useState('');

  useEffect(() => {
    if (filterName.length === 0) {
      setIsDisabled(true);
      setErrorMessage('');
    } else if (filterName.length > MAX_NAME_LENGTH) {
      setIsDisabled(true);
      setErrorMessage(ERROR_LONG_MESSAGE);
    } else if (filterCriteria?.length > 0) {
      setIsDisabled(false);
      setErrorMessage('');
    }
  }, [filterName, filterCriteria]);

  useEffect(() => {
    setIsDisabled(true);
    setErrorMessage('');
    setFilterName('');
  }, [isOpen]);

  useEffect(() => {
    setErrorMessage(filterNameErrorMessage);
  }, [filterNameErrorMessage]);

  useEffect(() => {
    if (filterCriteria?.length === 0) {
      setIsDisabled(true);
      setErrorMessage(ERROR_NO_FILTERS);
    }
  }, [filterCriteria]);

  const handleOnChange = (value: string) => {
    setFilterName(value);
    onFilterNameChange?.(value);
  };

  const handleOnSave = () => {
    onSave(filterName);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header>Save filter</Modal.Header>
      <Modal.Body>
        <div className="flex flex-column gap-8">
          {!useNewFilterLayout && filterCriteria?.length > 0 && (
            <FilterCriteriaComponent filterCriteria={filterCriteria} />
          )}
          <Form.Section label="Filter name">
            <TextInput
              autoFocus
              value={filterName}
              onChange={handleOnChange}
              placeholder="Enter filter name"
              hasError={!!errorMessage}
              errorMessage={errorMessage}
            />
          </Form.Section>
          {useNewFilterLayout && (
            <div className="flex flex-column gap-6">
              {isFiltersGrouped(filterCriteria)
                ? filterCriteria.map((group: FilterCriteriaGroup) => (
                    <ClassifiedFilters key={group.name} filters={group.filters} />
                  ))
                : filterCriteria?.length > 0 && <ClassifiedFilters filters={filterCriteria} />}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} mode="secondary" testId="cancel-button">
          Cancel
        </Button>
        <Button onClick={handleOnSave} type="submit" testId="save-button" disabled={isDisabled}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const FilterCriteriaComponent = ({ filterCriteria }: { filterCriteria: FilterCriteria[] | FilterCriteriaGroup[] }) => {
  return (
    <Form.Section label="Criteria">
      {isFiltersGrouped(filterCriteria) ? (
        filterCriteria.map((group: FilterCriteriaGroup) => (
          <FormControl key={group.name} name="groupName" label={`${group.name}:`}>
            <FilterChips filters={group.filters} />
          </FormControl>
        ))
      ) : (
        <FilterChips filters={filterCriteria} />
      )}
    </Form.Section>
  );
};

const FilterChips = ({ filters }: { filters: FilterCriteria[] }) => (
  <Chips>
    <Chips.List>
      {filters.map((filter: FilterCriteria) =>
        Array.isArray(filter.value) ? (
          <Chips.Chip key={filter.value.join(', ')} value={filter.value.join(', ')} label={filter.name} />
        ) : (
          <Chips.Chip key={filter.value} value={filter.value} label={filter.name} />
        )
      )}
    </Chips.List>
  </Chips>
);

const ClassifiedFilters = ({ filters }: { filters: FilterCriteria[] }) => (
  <>
    {filters.map((group: FilterCriteria) => {
      const values = Array.isArray(group.value) ? group.value : group.value.split(',');
      return (
        <div key={group.name} className="flex flex-column gap-3">
          <p className="text-lg font-bold">{group.name}</p>
          <Chips>
            <Chips.List>
              {values.map(value => (
                <Chips.Chip key={value} value={value} />
              ))}
            </Chips.List>
          </Chips>
        </div>
      );
    })}
  </>
);
